import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: May 31, 2026</p>

        <div className="prose prose-gray max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">1. Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed">
              Jiffys Assistance ("we", "our", "us") collects the following information when you use our mobile application and website:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-2 mt-3">
              <li><strong>Account Information:</strong> Name, email address, phone number, and password when you create an account.</li>
              <li><strong>Location Data:</strong> Real-time GPS location to connect you with nearby service providers and provide accurate ETAs. Location is collected when the app is in use and, for service providers, when background location is enabled during active shifts.</li>
              <li><strong>Vehicle Information:</strong> Make, model, year, and color of your vehicle when requesting services.</li>
              <li><strong>Payment Information:</strong> Credit/debit card details processed securely through Stripe. We do not store full card numbers on our servers.</li>
              <li><strong>Photos:</strong> Service completion photos, profile pictures, and identification documents uploaded for verification.</li>
              <li><strong>Usage Data:</strong> App interaction data, device type, operating system, and crash reports.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">2. How We Use Your Information</h2>
            <ul className="list-disc ml-6 text-gray-700 space-y-2">
              <li>To provide and improve our roadside assistance, mobile mechanic, and delivery services.</li>
              <li>To match you with available service providers based on your location.</li>
              <li>To process payments and handle billing.</li>
              <li>To verify the identity of service providers through background checks.</li>
              <li>To communicate with you about your service requests and account.</li>
              <li>To ensure safety through photo proof of service completion.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">3. Information Sharing</h2>
            <p className="text-gray-700 leading-relaxed">
              We do not sell your personal information. We share data only with:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-2 mt-3">
              <li><strong>Service Providers:</strong> Your name, location, and vehicle info are shared with your assigned provider only after they accept your job.</li>
              <li><strong>Payment Processors:</strong> Stripe processes your payment information securely.</li>
              <li><strong>Background Check Services:</strong> Provider identity information is shared with Checkr for background verification.</li>
              <li><strong>Law Enforcement:</strong> When required by law or to protect safety.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">4. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We use industry-standard encryption (TLS/SSL) for data in transit and at rest. Payment data is handled by PCI-compliant processors. Access to user data is restricted to authorized personnel only.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">5. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed">
              You may request access to, correction of, or deletion of your personal data at any time by contacting support@jiffysassistance.site. You can also disable location permissions in your device settings, though this may affect service functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">6. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are not intended for users under 18 years of age. We do not knowingly collect information from children.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">7. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about this Privacy Policy, contact us at:<br />
              Email: support@jiffysassistance.site<br />
              Phone: (260) 218-3536<br />
              Website: jiffysassistance.site
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
